import Controller, { inject as controller } from '@ember/controller';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { v1 } from 'ember-uuid';
import { task, dropTaskGroup, restartableTask, restartableTaskGroup } from 'ember-concurrency';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { SELLER_POSTINGS_FIND_WORK_TAB_NAMES } from 'sh-www/constants/routes/postings';
import { POSTING__ASSIGNMENT_SELLER_INCLUDES } from 'sh-www/constants/routes/posting';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { POSTINGS_SORT_API_ENUMS } from 'sh-www/constants/routes/postings';
import { htmlEntitiesDecode } from 'sh-www/utils/misc';
import { showModal } from 'sh-www/utils/modal-controls';
import clsx from 'clsx';

const TAB_NAME_TO_ROUTE_MAP = {
  [SELLER_POSTINGS_FIND_WORK_TAB_NAMES.SAVED]: EMBER_ROUTE_NAMES.POSTINGS__FIND_WORK__SAVED,
  [SELLER_POSTINGS_FIND_WORK_TAB_NAMES.OTHER]: EMBER_ROUTE_NAMES.POSTINGS__FIND_WORK__OTHER,
  [SELLER_POSTINGS_FIND_WORK_TAB_NAMES.RECOMMENDED]: EMBER_ROUTE_NAMES.POSTINGS__FIND_WORK__RECOMMENDED,
};

/**
 * @type {string[]}
 */
const QUERY_PARAMS = Object.freeze([
  'sort',
  'secondary_sort',
  'place_id',
  'minimum_day_rate',
  'remote',
  'has_no_fees',
  'covers_expenses',
  'user_offered_services',
]);

/**
 * @type {{ deadline: string, posted: string }}
 */
const SORT_OPTIONS = {
  posted: 'Posting date',
  deadline: 'Expiration date',
};

/**
 * @type {{ deadline: string, posted: string }}
 */
const DEFAULT_SORT_ORDER = {
  posted: 'desc',
  deadline: 'asc',
  [POSTINGS_SORT_API_ENUMS.SELLER_RELEVANCE]: 'asc',
};

const ASSIGNMENT_FORMATS_FOR_TABS = [
  ASSIGNMENT.FORMATS.RECOMMENDED,
  ASSIGNMENT.FORMATS.OTHER,
  ASSIGNMENT.FORMATS.SAVED,
];

/**
 * @param {{}} params
 * @return {string}
 */
const serializeParams = (params) => QUERY_PARAMS.reduce((accum, key) => {
  const value = params[key];

  if (value) {
    accum.push(`${key}=${value}`);
  }

  return accum;
}, []).join('&');

export const sellerPostingsCache = (queryParams) => {
  const cachedParams = { ...queryParams };
  const cache = new Map();
  const cacheKey = v1();

  return Object.freeze({
    get sort() {
      return cachedParams.sort || 'posted';
    },

    get secondarySort() {
      return cachedParams.secondary_sort || DEFAULT_SORT_ORDER[this.sort];
    },

    get placeId() {
      return cachedParams.place_id || null;
    },

    get minimumDayRate() {
      return cachedParams.minimum_day_rate || null;
    },

    get isRemote() {
      return cachedParams.remote || null;
    },

    get hasNoFees() {
      return cachedParams.has_no_fees || null;
    },

    get coversExpenses() {
      return cachedParams.covers_expenses || null;
    },

    get userOfferedServices() {
      return cachedParams.user_offered_services || null;
    },

    get cacheKey() {
      return cacheKey;
    },

    getPage(page) {
      return cache.get(page);
    },

    setPage(page, value) {
      cache.set(page, value);
      return value;
    },

    isValid(queryParams) {
      return serializeParams(cachedParams) === serializeParams(queryParams);
    },
  });
};

/**
 * @param {string} view
 * @return {Controller}
 */
export const postingsSellerControllerFactory = (view) => {
  return class SellerPostingsViewController extends Controller {
    queryParams = QUERY_PARAMS;
    sort = null;
    secondary_sort = null;
    place_id = null;
    minimum_day_rate = null;
    remote = null;
    has_no_fees = null;
    covers_expenses = null;
    user_offered_services = null;

    data = [];
    meta = null;
    originalCount = 0;
    selectedIndex = -1;
    location = null;
    formattedAddressForPlaceId = '';
    cache = null;

    @service('commands/assignment-commands')
    assignmentCommandService;

    @service('commands/activity-feed-item-commands')
    activityFeedItemCommands;

    @service('state-managers/application')
    applicationStateManager;

    @service
    session;

    @service
    store;

    @service
    api;

    @controller
    application;

    @controller('postings.find-work.saved')
    savedController;

    /**
     * if the user is not vetted, fetch all postings so we have something to
     * show behind the modal
     */
    get format() {
      const { currentUser } = this.session;
      if (currentUser.underReview) return 'all';
      if (currentUser.whitelistedAndUnvetted) return 'all';
      return view;
    }

    @computed('format')
    get isRecommendedFormat() {
      return ASSIGNMENT.FORMATS.RECOMMENDED === this.format;
    }

    @computed('format')
    get isOtherFormat() {
      return ASSIGNMENT.FORMATS.OTHER === this.format;
    }

    @computed('format')
    get isSavedFormat() {
      return ASSIGNMENT.FORMATS.SAVED === this.format;
    }

    @computed('format')
    get isTabFormat() {
      return ASSIGNMENT_FORMATS_FOR_TABS.includes(this.format);
    }

    get showResults() {
      return true;
    }

    @computed('data.[]', 'selectedIndex')
    get selectedItem() {
      return this.data[this.selectedIndex] ?? null;
    }

    @computed('meta.pagination.total')
    get total() {
      return this.meta?.pagination.total ?? 0;
    }

    @computed('meta.pagination.total_pages')
    get totalPages() {
      return this.meta?.pagination.total_pages ?? 0;
    }

    @computed('meta.pagination.current_page')
    get currentPage() {
      return this.meta?.pagination.current_page ?? 0;
    }

    @computed('meta.pagination.per_page')
    get pageSize() {
      return this.meta?.pagination.per_page ?? 0;
    }

    @computed('data.[]', 'currentPage', 'totalPages', 'selectedIndex')
    get hasNext() {
      const { currentPage, totalPages, data, selectedIndex } = this;

      if (selectedIndex < 0) return false;
      if (selectedIndex < data.length - 1) return true;
      if (currentPage < totalPages) return true;

      return false;
    }

    @computed('currentPage', 'selectedIndex')
    get hasPrevious() {
      const { currentPage, selectedIndex } = this;

      if (selectedIndex > 0) return true;
      if (currentPage > 1) return true;

      return false;
    }

    @dropTaskGroup
    modelUpdates;

    @restartableTaskGroup
    modelInvalidation;

    @readOnly('modelUpdates.isRunning')
    loading;

    @readOnly('cache.placeId')
    placeIdFilter;

    @readOnly('cache.minimumDayRate')
    minimumDayRateFilter;

    @readOnly('cache.isRemote')
    isRemoteFilter;

    @readOnly('cache.hasNoFees')
    hasNoFeesFilter;

    @readOnly('cache.coversExpenses')
    coversExpensesFilter;

    @readOnly('cache.userOfferedServices')
    userOfferedServicesFilter;

    @readOnly('cache.sort')
    sortValue;

    @readOnly('cache.secondarySort')
    secondarySortValue;

    @computed('cache.sort')
    get sortOptions() {
      const { sort } = this.cache;

      return Object.entries(SORT_OPTIONS).map(([value, title]) => ({
        title,
        value,
        selected: value === sort,
      }));
    }

    @computed('loading','isRecommendedFormat')
    get showSettings() {
      return !this.loading && this.isRecommendedFormat;
    }

    @computed(...QUERY_PARAMS)
    get hasFilterSet() {
      return QUERY_PARAMS.some((param) => this[param]);
    }

    modelIsValid(queryParams) {
      return this.cache.isValid(queryParams);
    }

    canAdvancePage(newPage) {
      return (newPage > 0) && (newPage <= this.totalPages);
    }

    decreaseTabCount() {
      const { originalCount } = this;
      const newCount = Math.max(0, originalCount - 1);
      this.set('originalCount', newCount);
    }

    increaseTabCount() {
      const { originalCount } = this;
      this.set('originalCount', originalCount + 1);
    }

    hideAssignmentFromUI(assignment, notInterested) {
      const { data } = this;
      const deleteIndex = data.findIndex(({ id }) => id === assignment.id);

      // set a temporary flag so the UI knows to hide it
      const updatedData = [...data];
      if (updatedData[deleteIndex]) {
        // if not already hidden by another async action
        updatedData[deleteIndex].set('hiddenFromUI', notInterested);
      }

      this.set('data', updatedData);
    }

    showNotInterestedUndoToast(assignment) {
      const assignmentTitle = htmlEntitiesDecode(assignment.title);
      this.applicationStateManager.addToast(
        `Not interested in '${assignmentTitle}'`,
        {
          text: 'Undo',
          action: () => {
            this.setNotInterested.perform(assignment, false);
          },
        },
      );
    }

    @action
    placeIdFilterChanged(location) {
      const placeId = location?.placeId ?? null;
      this.setProperties({
        'place_id': placeId,
        // clear conflicting filter
        'remote': null,
      });
    }

    @action
    formattedAddressForPlaceIdChanged(value) {
      this.set('formattedAddressForPlaceId', value);
    }

    @action
    minimumDayRateFilterChanged(minimumDayRate) {
      this.set('minimum_day_rate', minimumDayRate || null);
    }

    @action
    remoteFilterChanged(isRemote) {
      const remote = isRemote || null;
      this.setProperties({
        'remote': remote,
        // clear conflicting filter
        'place_id': null,
        'formattedAddressForPlaceId': '',
      });
    }

    @action
    hasNoFeesFilterChanged(hasNoFees) {
      this.set('has_no_fees', hasNoFees || null);
    }

    @action
    coversExpensesFilterChanged(coversExpenses) {
      this.set('covers_expenses', coversExpenses || null);
    }

    @action
    userOfferedServicesFilterChanged(useUserOfferedServices) {
      this.set('user_offered_services', useUserOfferedServices || null);
    }

    @action
    sortValueChanged(sort) {
      this.setProperties({ sort, secondary_sort: null });
    }

    @action
    secondarySortValueChanged(secondarySort) {
      this.set('secondary_sort', secondarySort);
    }

    /**
     * @param {DS.Model} assignment
     * @param {boolean} favorite
     */
    @action
    setFavorite(assignment, favorite) {
      return this.assignmentCommandService.setFavorite(assignment, this.session.currentUser.id, favorite).then((res) => {
        // reload saved tab
        this.savedController.reloadModel.perform(QUERY_PARAMS, true);

        return res;
      });
    }

    /**
     * @param {DS.Model} assignment
     */
    @action
    markViewed(assignment) {
      return this.activityFeedItemCommands.markRecentPostingItemRead(assignment);
    }

    /**
     * @param {DS.Model} assignment
     */
    @action
    showFeedback(assignment) {
      if (this.setNotInterested.isRunning) return;

      const notInterested = assignment.assignmentUserMeta?.notInterested;
      if (notInterested || !assignment.allowSellerFeedback) {
        this.setNotInterested.perform(assignment, !notInterested);
      } else {
        showModal(this, 'posting-feedback', {
          assignment,
          confirmAction: () => {
            this.showNotInterestedUndoToast(assignment);
            this.hideAssignmentFromUI(assignment, true);
            this.decreaseTabCount();
          },
        });
      }
    }

    /**
     * @param {DS.Model} assignment
     */
    @action
    showReferralModal(assignment) {
      showModal(this, 'refer-a-friend', { assignment });
    }

    /**
     * @param {Ember.Component} component
     * @param {number} selectedIndex
     */
    @action
    showSidebar(component, selectedIndex) {
      this.setProperties({ selectedIndex, 'application.postingsCarousel': component });
    }

    /**
     *
     */
    @action
    closeSidebar() {
      this.setProperties({ selectedIndex: -1, 'application.postingsCarousel': null });
    }

    @task({ group: 'modelUpdates' })
    *initializeModel(queryParams, updateCounts = false) {
      this.set('cache', sellerPostingsCache(queryParams));
      yield this.updateModel.perform(1);

      // set pagination counts now rather to handle manually
      const { meta } = this;
      if (updateCounts && meta) {
        if (this.isOtherFormat && this.hasFilterSet) {
          yield this.setDefaultExplorePaginationCount.perform();
        } else {
          this.set('originalCount', meta.pagination.total);
        }
      }
    }

    /**
     * @param {number} increment
     */
    @task({ group: 'modelUpdates' })
    *updateSelected(increment) {
      const newSelectedIndex = this.selectedIndex + increment;

      if ((0 <= newSelectedIndex) && (newSelectedIndex < this.data.length)) {
        this.set('selectedIndex', newSelectedIndex);
        return;
      }

      const newPage = this.currentPage + increment;

      if (this.canAdvancePage(newPage)) {
        yield this.updateModel.perform(newPage);
        this.set('selectedIndex', increment > 0 ? 0 : this.data.length - 1);
      }
    }

    @task({ group: 'modelUpdates' })
    *updatePage(increment) {
      const newPage = this.currentPage + increment;

      if (this.canAdvancePage(newPage)) {
        window.scrollTo({ top: 0 });
        yield this.updateModel.perform(newPage);
      }
    }

    @task({ group: 'modelInvalidation' })
    *reloadModel(queryParams, updateCounts = false) {
      yield this.modelUpdates.cancelAll();
      this.initializeModel.perform(queryParams, updateCounts);
    }

    @task({ group: 'modelInvalidation' })
    *invalidateModel() {
      yield this.modelUpdates.cancelAll();

      const queryParams = QUERY_PARAMS.reduce((accum, param) => {
        accum[param] = null;
        return accum;
      }, { });

      this.setProperties({
        ...queryParams,
        data: [],
        meta: null,
        originalCount: 0,
        selectedIndex: -1,
        cache: null,
      });
    }

    /**
     *
     * @param {number} page
     */
    @restartableTask
    *updateModel(page) {
      // avoid loading when not shown
      if (!this.session.currentUser.isVetted) {
        return;
      }

      const cache = this.cache;
      const cachedResponse = cache.getPage(page);

      if (cachedResponse) {
        this.setProperties({ ...cachedResponse, selectedIndex: -1 });
        return;
      }

      const assignmentIncludes = [...POSTING__ASSIGNMENT_SELLER_INCLUDES];
      assignmentIncludes.push('applications');
      assignmentIncludes.push('assignment-user-meta');

      // respect default sort on tabs
      const sort = ASSIGNMENT_FORMATS_FOR_TABS.includes(this.format) ?
        POSTINGS_SORT_API_ENUMS.SELLER_RELEVANCE
        : cache.sort;

      const params = {
        page,
        sort,
        secondary_sort: cache.secondarySort,
        cache_key: cache.cacheKey,
        count: 10,
        include: assignmentIncludes.join(','),
        filter: {},
        format: this.format,
      };

      if (cache.placeId) {
        params.place_id = cache.placeId;
        params.filter.place_id = cache.placeId;
      }

      if (cache.minimumDayRate) {
        params.filter.minimum_day_rate = cache.minimumDayRate;
      }

      if (cache.isRemote) {
        params.filter.remote = cache.isRemote;
      }

      if (cache.hasNoFees) {
        params.filter.has_no_fees = cache.hasNoFees;
      }

      if (cache.coversExpenses) {
        params.filter.covers_expenses = cache.coversExpenses;
      }

      if (cache.userOfferedServices) {
        params.filter.user_offered_services = cache.userOfferedServices;
      }

      const response = yield this.api.request('postings', { params });

      const store = this.store;
      store.pushPayload(response);

      const data = response.data.map(({ id }) => store.peekRecord('assignment', id));
      const meta = response.meta;
      cache.setPage(page, { data, meta });

      this.setProperties({ data, meta, selectedIndex: -1 });
    }

    @task
    *setNotInterested(assignment, notInterested) {
      yield this.assignmentCommandService.setNotInterested(assignment, this.session.currentUser.id, {}, notInterested);

      if (notInterested) {
        this.showNotInterestedUndoToast(assignment);
      }

      this.hideAssignmentFromUI(assignment, notInterested);

      if (notInterested) {
        this.decreaseTabCount();

        // reload saved tab
        this.savedController.reloadModel.perform(QUERY_PARAMS, true);
      } else {
        this.increaseTabCount();
      }
    }

    @task
    *setDefaultExplorePaginationCount() {
      const params = {
        page: 1,
        count: 1,
        format: ASSIGNMENT.FORMATS.OTHER,
      };

      try {
        const response = yield this.api.request('postings', { params });
        const meta = response.meta;
        this.set('originalCount', meta.pagination.total);
      } catch (e) {
        const { meta } = this
        if (this.meta) {
          this.set('originalCount', meta.pagination.total);
        }
      }
    }
  }
};

/**
 * @param {string} name
 * @return {Ember.ComputedProperty}
 */
const pageTotal = (name) => readOnly(`${name}Controller.originalCount`);

/**
 * @param name
 * @return {Ember.ComputedProperty}
 */
const isRoute = (name) => computed('router.currentRouteName', function() {
  switch (this.router.currentRouteName) {
    case `postings.${name}`:
    case `postings.${name}_loading`: return true;
    default: return false;
  }
});

const SEARCH_QUERY = Object.freeze({
  place_id: null,
  minimum_day_rate: null,
  remote: null,
  has_no_fees: null,
  covers_expenses: null,
  user_offered_services: null,
  sort: null,
  secondary_sort: null,
});

/** @module */
class SellerPostingsIndexController extends Controller {
  @service router;
  @service session;

  @controller('postings.find-work.recommended')
  recommendedController;

  @controller('postings.find-work.other')
  otherController;

  @controller('postings.find-work.saved')
  savedController;

  @controller('postings.search')
  searchController;

  @isRoute('search')
  isSearchRoute;

  @pageTotal('recommended')
  recommendedCount;

  @pageTotal('other')
  otherCount;

  @pageTotal('saved')
  savedCount;

  @readOnly('searchController.location')
  location;

  activeNavItem = SELLER_POSTINGS_FIND_WORK_TAB_NAMES.RECOMMENDED;

  get searchQuery() {
    return SEARCH_QUERY;
  }

  @computed('showBlur')
  get classNames() {
    return clsx('seller-postings', this.showBlur && 'seller-postings--blur');
  }

  @computed('session.currentUser.isVetted')
  get showBlur() {
    return !this.session.currentUser.isVetted;
  }

  @computed('recommendedCount', 'otherCount', 'savedCount')
  get countsObj() {
    return {
      [SELLER_POSTINGS_FIND_WORK_TAB_NAMES.RECOMMENDED]: this.recommendedCount,
      [SELLER_POSTINGS_FIND_WORK_TAB_NAMES.SAVED]: this.savedCount,
      [SELLER_POSTINGS_FIND_WORK_TAB_NAMES.OTHER]: this.otherCount,
    }
  }

  @computed('activeNavItem', 'countsObj')
  get navItems() {
    return Object.keys(SELLER_POSTINGS_FIND_WORK_TAB_NAMES).map((tabKey) => {
      const tabName = SELLER_POSTINGS_FIND_WORK_TAB_NAMES[tabKey];

      return {
        title: tabName,
        isActive: () => this.activeNavItem === tabName,
        count: this.countsObj[tabName],
      }
    });
  }

  @action
  onNavItemClicked(tab) {
    if (this.activeNavItem === tab) {
      return;
    }

    const selectedRoute = TAB_NAME_TO_ROUTE_MAP[tab];
    if (selectedRoute) {
      this.router.transitionTo(selectedRoute);
    } else {
      throw new Error('Invalid nav item clicked');
    }
  }
}

export default SellerPostingsIndexController;
